import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { ProductAttributes, ProductCategories } from "@models/product.model";
import { AuthService } from "../../services/auth/auth.service";
import { WishlistService } from "../../services/wishlistService/wishlist.service";
import { DialogService } from "../../services/dialog/dialog.service";
import { Router } from "@angular/router";
import { BaseCart, CartAttributes, RemoveCartItem } from "@models/cart.model";
import { CartItemService } from "../../services/cart-item/cart-item.service";
import { debounceTime, Subject } from "rxjs";
import { LocalStorageService } from "../../services/local-storage/local-storage.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { SnackBarConfig } from "../../constants/snackbar.constants";
import { v4 as uuidv4 } from "uuid";
import { GenericDialogComponent } from "../generic-dialog/generic-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { DialogInvokingComponents } from "@models/generic-dialog.model";
import { TranslateService } from "@ngx-translate/core";

@Component({
    selector: "app-product-card",
    templateUrl: "./product-card.component.html",
    styleUrl: "./product-card.component.scss",
    standalone: false,
})
export class ProductCardComponent implements OnInit {
    @Input() product!: ProductAttributes;
    public productCategories = ProductCategories;
    private cartUpdateSubject = new Subject<{ productId: number; quantity: number }>();
    public cartItems: CartAttributes[] = [];
    public isLoading = false;
    @Output() isNavbarOpen = new EventEmitter<boolean>();
    @Input() imageBgClass: string = "";
    @Input() isWishlist: boolean = false;

    constructor(
        private authService: AuthService,
        private wishListItemService: WishlistService,
        private dialogService: DialogService,
        private router: Router,
        private cartService: CartItemService,
        private localStorageService: LocalStorageService,
        private snackBar: MatSnackBar,
        private dialog: MatDialog,
        private translate: TranslateService,
    ) {}

    ngOnInit(): void {
        this.cartUpdateSubject
            .pipe(
                debounceTime(250), // Adjust the time as needed
            )
            .subscribe(({ productId, quantity }) => {
                this.updateCartQuantity(productId, quantity);
            });
        this.cartService.cartItems$.subscribe((response) => {
            this.cartItems = response;
        });
    }

    public isCategoryBuyBlocks(category?: string[] | undefined): boolean {
        return Array.isArray(category) && category.includes(this.productCategories.BuyBlocks);
    }

    public toogleFavorite(product: ProductAttributes): void {
        if (this.authService.isUserLoggedIn) {
            if (this.isInWishlist(product)) {
                this.wishListItemService.deleteFromMyWishlist(product.id).subscribe();
            } else {
                this.wishListItemService.addToWishList([product.id]).subscribe();
            }
        } else {
            this.dialogService.loginDialog();
        }
    }

    public isInWishlist(product: ProductAttributes): boolean {
        if (this.authService.isUserLoggedIn) {
            return this.wishListItemService.getAllwishlistIDs?.productIDs.includes(product.id);
        } else {
            return false;
        }
    }

    public getTruncatedRating(rating: number): number {
        return Math.floor(rating); // or use Math.trunc(rating)
    }

    public onClickProduct(productId: number): void {
        this.router.navigate([`products/${productId}`]);
    }

    public addToCart(product: ProductAttributes, quantity: number, isWishlist: boolean = false): void {
        this.isLoading = true;
        // You can implement toast notification here
        const item: BaseCart = {
            productID: product.id,
            quantity: quantity,
            cartUUID: uuidv4(),
        };
        this.cartService.addToCart(item, product).subscribe((response) => {
            if (response) {
                if (isWishlist) {
                    this.wishListItemService.wishlistSignal.next(product.id);
                } else {
                    this.openSnackBar(response, SnackBarConfig.Actions.OKAY);
                    this.isLoading = false;
                    this.isNavbarOpen.emit(true);
                }
            }
        });
    }

    public onCustomize(productId: number): void {
        this.router.navigate(["products", productId]);
    }

    public getProductQtyInCart(productId: number): number {
        return this.localStorageService.getCartedQuantity(productId);
    }

    public onclick(value: number, productId: number): void {
        const quantityInCart = this.getProductQtyInCart(productId);
        const quantity = quantityInCart + value;
        this.cartUpdateSubject.next({ productId, quantity });
        this.isNavbarOpen.emit(true);
    }

    public onInputChange(event: Event, productId: number): void {
        let inputValue = (event.target as HTMLInputElement).value.trim();
        let quantity = parseInt(inputValue, 10);

        if (isNaN(quantity)) {
            return;
        } else if (quantity < 1) {
            (event.target as HTMLInputElement).value = "1";
            quantity = parseInt((event.target as HTMLInputElement).value, 10);
        } else if (inputValue.length > 2) {
            inputValue = inputValue.slice(0, 2);
            (event.target as HTMLInputElement).value = inputValue;
            return;
        }

        this.cartUpdateSubject.next({ productId, quantity });
    }

    public onBlur(event: Event, productId: number): void {
        const inputValue = (event.target as HTMLInputElement).value.trim();
        if (!inputValue) {
            (event.target as HTMLInputElement).value = "1";
            const quantity = parseInt((event.target as HTMLInputElement).value, 10);
            this.cartUpdateSubject.next({ productId, quantity });
        }
    }

    private updateCartQuantity(productId: number, quantity: number, isWishlist: boolean = false): void {
        if (quantity < 1) {
            this.removeProductFromCart(productId);
        } else if (quantity <= 99) {
            this.updateProductQuantityInCart(productId, quantity, isWishlist);
        }
    }

    private removeProductFromCart(id: number): void {
        const item: RemoveCartItem = { productId: id };
        this.cartService.removeCartItem([item]).subscribe((response) => {
            if (this.cartItems.length && response) {
                this.isNavbarOpen.emit(true);
            } else {
                this.isNavbarOpen.emit(false);
            }
        });
    }

    private updateProductQuantityInCart(productId: number, quantity: number, isWishlist: boolean = false): void {
        this.cartService.updateCartQuantity(productId, quantity).subscribe(() => {
            if (isWishlist) {
                this.wishListItemService.wishlistSignal.next(productId);
            }
        });
    }

    public ratingCountWithouDecimal(averageRating: number): number {
        return Math.floor(averageRating);
    }

    public getStarsArray(): number[] {
        return Array.from({ length: 5 }, (_, i) => i);
    }

    public getFilledStars(): number {
        return Math.floor(this.product.averageRating!);
    }

    public formatPrice(price: number): string {
        return price.toLocaleString();
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    private openSnackBar(message: string, action: string): void {
        this.snackBar.open(message, action, {
            ...SnackBarConfig.Defaults,
        });
    }

    public deleteWishlistItem(item: ProductAttributes): void {
        this.openToremoveItemFromWishList(item);
    }

    // Open to Dialog for Remove item from Wishlist
    private openToremoveItemFromWishList(item: ProductAttributes) {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: {
                componentName: DialogInvokingComponents.RemoveFromWishlist,
                title: this.getTranslation("genericDialogBox.removeItemWishlist.title"),
                image: item.images?.imagesUrl[0],
                content: this.getTranslation("genericDialogBox.removeItemWishlist.content"),
                firstBtn: this.getTranslation("genericDialogBox.button.removeBtn"),
            },
            autoFocus: false,
            restoreFocus: false,
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.wishListItemService.wishlistSignal.next(item.id);
            }
        });
    }

    public moveToCart(product: ProductAttributes, quantity: number): void {
        this.cartService.getCartItems().subscribe((cartResponse) => {
            const cartItems = cartResponse;
            const existingItem = cartItems.find((item) => item.product?.id === product.id);
            if (existingItem && existingItem.product) {
                // Update quantity if the item is already in the cart
                this.updateCartQuantity(existingItem.product.id, ++existingItem.quantity!, true);
            } else {
                // Add new item to the cart if not exist
                this.addToCart(product, quantity, true);
            }
        });
    }
}
